import {
  COMMUNITY_REFERRALS_V2,
  HERITAGE_CREDIT_V2,
  PRO_BONO_V2,
  QA_NAV_HIDDEN,
  TEAMS_FEATURE_ENABLED
} from "../../constants/featureFlags";
import { getPaidLeadsBasePath } from "../payPerLead/basePath";
import { getProBonoBasePath } from "../proBono/basePath";

/**
 * The shell's navigation: four groups, the first without a title. `id` is the active-page id, `label` a translation key
 * (dashboard_shell_*), `icon` a name in icons.js. An item whose flag is off is left out of the list, not hidden by CSS.
 *
 * @returns {Array<{key: string, label: ?string, icon: ?string, items: Array<{id: string, label: string, icon: string, to: string, count?: boolean}>}>}
 */
export const buildNav = () => {
  const keep = list => list.filter(Boolean);

  return [
    {
      key: "main",
      label: null,
      icon: null,
      items: [
        { id: "feed", label: "dashboard_shell_item_feed", icon: "home", to: "/" },
        { id: "inbox", label: "dashboard_shell_item_inbox", icon: "inbox", to: "/inbox", count: true },
        { id: "profile", label: "dashboard_shell_item_profile", icon: "profile", to: "/profile" }
      ]
    },
    {
      key: "referrals",
      label: "dashboard_shell_group_referrals",
      icon: "referrals",
      items: keep([
        { id: "referrals", label: "dashboard_shell_item_referrals", icon: "referrals", to: "/referrals" },
        COMMUNITY_REFERRALS_V2 && {
          id: "community",
          label: "dashboard_shell_item_community",
          icon: "community",
          to: "/community-referrals"
        },
        !QA_NAV_HIDDEN && { id: "qa", label: "dashboard_shell_item_qa", icon: "qa", to: "/question-answer" },
        { id: "paid", label: "dashboard_shell_item_paid", icon: "paid", to: getPaidLeadsBasePath() },
        PRO_BONO_V2 && { id: "probono", label: "dashboard_shell_item_probono", icon: "probono", to: getProBonoBasePath() }
      ])
    },
    {
      key: "listings",
      label: "dashboard_shell_group_listings",
      icon: "orgs",
      items: [
        { id: "orgs", label: "dashboard_shell_item_orgs", icon: "orgs", to: "/my-organizations" },
        { id: "events", label: "dashboard_shell_item_events", icon: "events", to: "/my-events" },
        { id: "jobs", label: "dashboard_shell_item_jobs", icon: "jobs", to: "/my-jobs" }
      ]
    },
    {
      key: "account",
      label: "dashboard_shell_group_account",
      icon: "account",
      items: keep([
        HERITAGE_CREDIT_V2 && { id: "credit", label: "dashboard_shell_item_credit", icon: "credit", to: "/wallet" },
        { id: "account", label: "dashboard_shell_item_account", icon: "account", to: "/account" },
        { id: "notifications", label: "dashboard_shell_item_notifications", icon: "notification", to: "/manage-notification" },
        { id: "payments", label: "dashboard_shell_item_payments", icon: "payments", to: "/payments" },
        TEAMS_FEATURE_ENABLED && { id: "team", label: "dashboard_shell_item_team", icon: "team", to: "/teams" },
        { id: "affiliates", label: "dashboard_shell_item_affiliates", icon: "partners", to: "/affiliate" }
      ])
    }
  ];
};

/** [path prefix, active id], first match wins. A prefix matches the path itself and anything below it. */
const ACTIVE_RULES = [
  ["/inbox", "inbox"],
  ["/profile", "profile"],
  ["/referrals", "referrals"],
  ["/referral-requests", "referrals"],
  ["/community-referrals", "community"],
  ["/question-answer", "qa"],
  ["/paid-referrals", "paid"],
  ["/paid-leads", "paid"],
  ["/pro-bono-referrals", "probono"],
  ["/pro-bono", "probono"],
  ["/my-organizations", "orgs"],
  ["/listings", "orgs"],
  ["/my-events", "events"],
  ["/my-jobs", "jobs"],
  ["/wallet", "credit"],
  ["/account", "account"],
  ["/manage-notification", "notifications"],
  ["/notifications", "notifications"],
  ["/payments", "payments"],
  ["/teams", "team"],
  ["/affiliate", "affiliates"],
  ["/partners", "affiliates"],
  ["/following", "feed"],
  ["/saved", "feed"]
];

/**
 * The active-page id for a pathname, so the sidebar, the rail flyout and the account menu highlight the same item on every
 * route, legacy paths (/paid-leads, /pro-bono, /partners) included. "/" and anything unknown is My Feed.
 *
 * @param {string} pathname
 * @returns {string}
 */
export const activeNavId = pathname => {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  const rule = ACTIVE_RULES.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));

  return rule ? rule[1] : "feed";
};
